import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form, Spinner, Alert } from "reactstrap";
import Cookies from "js-cookie";

import Head from "../layout/head/Head";
import AuthFooter from "./auth/AuthFooter";
import LogoDark2x from "../images/logo-dark2x.png";
import authService from "../auth/authService";
import { ACCESS_TOKEN_COOKIE } from "../auth/tokenHelpers";
import {
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../components/Component";

interface SetPasswordForm {
  password: string;
  confirmpassword: string;
}

const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] =
    useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<SetPasswordForm>();

  const onFormSubmit = async (formData: SetPasswordForm) => {
    setLoading(true);
    setError("");
    try {
      // The reset-email link carries `?data=<json>` where json is
      // `{ userId: number, token: { access_token, ... } }`.
      const raw = new URLSearchParams(window.location.search).get("data");
      if (!raw) {
        setError("This reset link is invalid or has expired.");
        setLoading(false);
        return;
      }
      const parsed = JSON.parse(raw);

      // Store the reset token so the axios interceptor authorizes the call.
      Cookies.set(ACCESS_TOKEN_COOKIE, JSON.stringify(parsed.token), {
        expires: 7,
        secure: true,
        sameSite: "Strict",
      });

      const resData = await authService.setPassword({
        id: parsed.userId,
        password: formData.password,
      });

      if (resData?.status) {
        navigate("/login", {
          state: {
            message:
              "Your password has been set. Please sign in with your new password.",
          },
        });
      } else {
        setError(resData?.message || "Unable to set password. Please try again.");
        setLoading(false);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to set password. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <>
      <Head title="Set Password" />
      <div className="login-container pt-2">
        <div className="brand-logo pb-1 text-center">
          <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
            <img className="" src={LogoDark2x} alt="logo" />
          </Link>
        </div>
        <div className="d-flex justify-content-center h-100">
          <Block className="nk-block-middle nk-auth-body wide-xs w-50 pb-0">
            <PreviewCard
              className="login-previewCard card-bordered"
              bodyClass="card-inner-lg"
            >
              <BlockHead>
                <BlockContent>
                  <BlockTitle tag="h4">Set Password</BlockTitle>
                  <BlockDes>
                    <p>Set your new password to access ProcureX.</p>
                  </BlockDes>
                </BlockContent>
              </BlockHead>

              {errorVal && (
                <div className="mb-3">
                  <Alert color="danger" className="alert-icon">
                    <Icon name="alert-circle" />
                    {errorVal}
                  </Alert>
                </div>
              )}

              <Form className="is-alter" onSubmit={handleSubmit(onFormSubmit)}>
                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="password">
                      Password
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <a
                      href="#password"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setPasswordVisible(!passwordVisible);
                      }}
                      className={`form-icon lg form-icon-right passcode-switch ${
                        passwordVisible ? "is-hidden" : "is-shown"
                      }`}
                    >
                      <Icon name="eye" className="passcode-icon icon-show" />
                      <Icon name="eye-off" className="passcode-icon icon-hide" />
                    </a>
                    <input
                      type={passwordVisible ? "text" : "password"}
                      id="password"
                      {...register("password", {
                        required: "This field is required",
                        pattern: {
                          value:
                            /^(?=.*[A-Z])(?=.*[@$#^_-])[A-Za-z\d@$#^_-]{8,}$/,
                          message:
                            "Password must be at least 8 characters and include an uppercase letter, a number, and one of @ $ # ^ _ -",
                        },
                      })}
                      placeholder="Enter your password"
                      className="form-control-lg form-control"
                    />
                    {errors.password && (
                      <span className="invalid">{errors.password.message}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="confirmpassword">
                      Confirm Password
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <a
                      href="#confirmpassword"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setConfirmPasswordVisible(!confirmPasswordVisible);
                      }}
                      className={`form-icon lg form-icon-right passcode-switch ${
                        confirmPasswordVisible ? "is-hidden" : "is-shown"
                      }`}
                    >
                      <Icon name="eye" className="passcode-icon icon-show" />
                      <Icon name="eye-off" className="passcode-icon icon-hide" />
                    </a>
                    <input
                      type={confirmPasswordVisible ? "text" : "password"}
                      id="confirmpassword"
                      {...register("confirmpassword", {
                        required: "This field is required",
                        validate: (value) =>
                          value === getValues("password") ||
                          "Passwords must match",
                      })}
                      placeholder="Confirm your password"
                      className="form-control-lg form-control"
                    />
                    {errors.confirmpassword && (
                      <span className="invalid">
                        {errors.confirmpassword.message}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <Button
                    size="lg"
                    className="btn-block"
                    type="submit"
                    color="primary"
                  >
                    {loading ? <Spinner size="sm" color="light" /> : "Submit"}
                  </Button>
                </div>

                <div className="form-note-s2 text-center pt-2">
                  <Link className="link link-primary" to="/login">
                    <Icon name="arrow-left" /> Back to Sign-In
                  </Link>
                </div>
              </Form>
            </PreviewCard>
          </Block>
        </div>
        <div className="login-bgi"></div>
      </div>
      <AuthFooter />
    </>
  );
};

export default SetPassword;
